import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, putDataAPI } from '../../utils/fetchData';
import { useDispatch } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../../store/alert/alertSlice';

const WorkflowDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { workflowId } = useParams();
  
  const [workflow, setWorkflow] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // all, overdue, due-today, completed
  const [showStepSettings, setShowStepSettings] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    dueDate: '',
    description: ''
  });

  useEffect(() => {
    loadWorkflow();
  }, [workflowId]);

  const loadWorkflow = async () => {
    try {
      setLoading(true);
      
      console.log('🔍 WorkflowDetail - Loading workflow with ID:', workflowId);
      
      // Load reminders from backend API
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/reminders', token);
      
      if (response?.status !== 200 && !response?.data?.success) {
        console.error('❌ Failed to load reminders:', response);
        throw new Error('Failed to load reminders');
      }
      
      const allReminders = response.data.data || [];
      console.log('🔍 WorkflowDetail - After API call, got', allReminders.length, 'reminders');
      console.log('🔍 WorkflowDetail - Sample reminders:', allReminders.slice(0, 3).map(r => ({
        id: r._id,
        releaseId: r.releaseId,
        releaseName: r.releaseName,
        title: r.title,
        type: r.type,
        metadata: r.metadata
      })));
      
      // Try to find workflow steps by releaseId first, then by workflowId in metadata
      let workflowSteps = allReminders.filter(r => r.releaseId === workflowId);
      console.log('🔍 WorkflowDetail - Found by releaseId:', workflowSteps.length);
      
      // If not found by releaseId, try by workflowId in metadata
      if (workflowSteps.length === 0) {
        workflowSteps = allReminders.filter(r => r.metadata?.workflowId === workflowId);
        console.log('🔍 WorkflowDetail - Found by metadata.workflowId:', workflowSteps.length);
      }
      
      if (workflowSteps.length === 0) {
        console.error('❌ Workflow not found with ID:', workflowId);
        console.log('🔍 Available reminders:', allReminders.map(r => ({ 
          id: r._id, 
          releaseId: r.releaseId, 
          workflowId: r.metadata?.workflowId,
          releaseName: r.releaseName,
          title: r.title 
        })));
        dispatch(setErrorAlert('Workflow not found'));
        navigate('/flows');
        return;
      }
      
      // Map backend fields to frontend format
      const mappedSteps = workflowSteps.map(step => ({
        id: step._id,
        releaseId: step.releaseId,
        releaseName: step.releaseName,
        releaseDate: step.releaseDate,
        title: step.title,
        description: step.description,
        status: step.status,
        dueDate: step.dueDate,
        type: step.type,
        metadata: step.metadata || {}
      }));
      
      // Sort by due date
      const sortedSteps = mappedSteps.sort((a, b) => 
        new Date(a.dueDate) - new Date(b.dueDate)
      );
      
      const completedCount = sortedSteps.filter(s => s.status === 'completed').length;
      const percentage = Math.round((completedCount / sortedSteps.length) * 100);
      
      setWorkflow({
        id: workflowId,
        name: sortedSteps[0].releaseName,
        date: sortedSteps[0].releaseDate,
        type: sortedSteps[0].type === 'booking' || sortedSteps[0].metadata?.type === 'booking' ? 'show' : 'release',
        completedSteps: completedCount,
        totalSteps: sortedSteps.length,
        percentage
      });
      
      setSteps(sortedSteps);
      
    } catch (error) {
      console.error('Error loading workflow:', error);
      dispatch(setErrorAlert('Failed to load workflow'));
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteStep = async (stepId) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await putDataAPI(`user/reminders/${stepId}/status`, {
        status: 'completed'
      }, token);
      
      if (response?.status === 200 || response?.data?.success) {
        dispatch(setSuccessAlert('Step completed'));
        loadWorkflow();
      } else {
        throw new Error('Failed to complete step');
      }
    } catch (error) {
      console.error('Error completing step:', error);
      dispatch(setErrorAlert('Failed to complete step'));
    }
  };

  const handleSnoozeStep = async (stepId) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const snoozeUntil = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours from now
      
      const response = await putDataAPI(`user/reminders/${stepId}/status`, {
        status: 'snoozed',
        snoozeUntil: snoozeUntil.toISOString()
      }, token);
      
      if (response?.status === 200 || response?.data?.success) {
        dispatch(setSuccessAlert('Step snoozed for 24 hours'));
        loadWorkflow();
      } else {
        throw new Error('Failed to snooze step');
      }
    } catch (error) {
      console.error('Error snoozing step:', error);
      dispatch(setErrorAlert('Failed to snooze step'));
    }
  };

  const handleEditClick = (step) => {
    setEditingStep(step.id);
    setEditForm({
      title: step.template?.title || '',
      dueDate: step.dueDate.split('T')[0], // Format for date input
      description: step.template?.description || ''
    });
    setShowStepSettings(null);
  };

  const handleSaveEdit = (stepId) => {
    try {
      const reminders = JSON.parse(localStorage.getItem('reminders') || '[]');
      const updatedReminders = reminders.map(reminder => {
        if (reminder.id === stepId) {
          return {
            ...reminder,
            template: {
              ...reminder.template,
              title: editForm.title,
              description: editForm.description
            },
            dueDate: new Date(editForm.dueDate).toISOString()
          };
        }
        return reminder;
      });
      
      localStorage.setItem('reminders', JSON.stringify(updatedReminders));
      window.dispatchEvent(new Event('localStorageUpdate'));
      
      dispatch(setSuccessAlert('Step updated successfully'));
      setEditingStep(null);
      loadWorkflow();
    } catch (error) {
      console.error('Error updating step:', error);
      dispatch(setErrorAlert('Failed to update step'));
    }
  };

  const handleCancelEdit = () => {
    setEditingStep(null);
    setEditForm({ title: '', dueDate: '', description: '' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysUntil = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const getStepStatus = (step) => {
    if (step.status === 'completed') return 'completed';
    if (step.isOverdue && step.isOverdue()) return 'overdue';
    if (step.isDueToday && step.isDueToday()) return 'due-today';
    
    const daysUntil = Math.ceil((new Date(step.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysUntil <= 3) return 'urgent';
    
    return 'upcoming';
  };

  const filterSteps = () => {
    if (activeTab === 'all') return steps;
    if (activeTab === 'completed') return steps.filter(s => s.status === 'completed');
    if (activeTab === 'overdue') return steps.filter(s => getStepStatus(s) === 'overdue');
    if (activeTab === 'due-today') return steps.filter(s => getStepStatus(s) === 'due-today');
    return steps;
  };

  const getStepNumber = (step) => {
    // Extract step number from template ID or use index
    const match = step.templateId.match(/\d+/);
    return match ? match[0] : steps.indexOf(step) + 1;
  };

  if (loading) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center">
            <div className="text-left py-[60px] px-4 w-full">
              <div style={{ 
                color: 'rgba(255, 255, 255, 0.5)', 
                fontSize: '12px', 
                fontWeight: '600',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center'
              }}>
                Loading workflow details
                <span className="loading-dots" style={{
                  marginLeft: '2px'
                }}></span>
              </div>
              <p style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '12px',
                lineHeight: '1.5',
                marginBottom: '24px',
                maxWidth: '300px'
              }}>
                Fetching your workflow steps and progress...
              </p>
              <style>{`
                @keyframes thinking {
                  0% { content: ''; }
                  33% { content: '.'; }
                  66% { content: '..'; }
                  100% { content: '...'; }
                }
                .loading-dots::after {
                  content: '';
                  animation: thinking 1.2s infinite;
                }
              `}</style>
            </div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  if (!workflow) {
    return null;
  }

  const filteredSteps = filterSteps();

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-start">
            <div className="flex-1">
              <h1 className="text-white text-[14px]" style={{ fontWeight: 300 }}>
                {workflow.name.toUpperCase()}
              </h1>
              <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-1">
                {workflow.type === 'show' ? 'Show: ' : 'Release: '}{formatDate(workflow.date)}
              </p>
              <p className="text-[rgba(255,255,255,0.5)] text-[11px]">
                Step {workflow.completedSteps}/{workflow.totalSteps} · {workflow.percentage}% Complete
              </p>
            </div>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70"
              onClick={() => navigate(-1)}
            >
              Go back
            </span>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6">
            <div className="flex">
              {['all', 'overdue', 'due-today', 'completed'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab === 'due-today' ? 'Due Today' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#FF3B30]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="w-full px-4 flex-1 overflow-y-auto">

            {/* Steps List */}
            <div className="space-y-3">
              {filteredSteps.map((step, index) => {
                const status = getStepStatus(step);
                const isCompleted = step.status === 'completed';
                
                return (
                  <div
                    key={step.id}
                    className={`bg-black border rounded-lg p-4 transition-all ${
                      isCompleted 
                        ? 'border-green-500 border-opacity-30' 
                        : status === 'overdue'
                        ? 'border-red-500'
                        : status === 'due-today'
                        ? 'border-yellow-500'
                        : 'border-[rgba(255,255,255,0.1)]'
                    }`}
                  >
                    {editingStep === step.id ? (
                      /* Edit Form */
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white text-[10px] opacity-50">
                            Step {index + 1}/{workflow.totalSteps}
                          </span>
                          <span className="text-[#FF3B30] text-[10px] font-medium">Editing</span>
                        </div>

                        {/* Edit Title */}
                        <div>
                          <label className="block text-white text-[10px] opacity-70 mb-1">Title</label>
                          <input
                            type="text"
                            value={editForm.title}
                            onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded p-2 text-white text-[11px] outline-none focus:border-[#17A2B8]"
                            placeholder="Enter title"
                          />
                        </div>

                        {/* Change Due Date */}
                        <div>
                          <label className="block text-white text-[10px] opacity-70 mb-1">Due Date</label>
                          <input
                            type="date"
                            value={editForm.dueDate}
                            onChange={(e) => setEditForm({...editForm, dueDate: e.target.value})}
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded p-2 text-white text-[11px] outline-none focus:border-[#17A2B8]"
                          />
                        </div>

                        {/* Edit Description */}
                        <div>
                          <label className="block text-white text-[10px] opacity-70 mb-1">Description</label>
                          <textarea
                            value={editForm.description}
                            onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded p-2 text-white text-[11px] outline-none focus:border-[#17A2B8] min-h-[60px]"
                            placeholder="Enter description"
                          />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => handleSaveEdit(step.id)}
                            className="flex-1 bg-[#FF3B30] text-white text-[10px] font-medium py-2 rounded hover:bg-[rgba(255,59,48,0.9)] transition-all"
                          >
                            Save Changes
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex-1 bg-[rgba(255,255,255,0.1)] text-white text-[10px] font-medium py-2 rounded hover:bg-[rgba(255,255,255,0.2)] transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Normal View */
                      <>
                        {/* Step Header */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white text-[10px] opacity-50">
                                Step {index + 1}/{workflow.totalSteps}
                              </span>
                              {isCompleted && (
                                <span className="text-green-500 text-[10px]">✓ Completed</span>
                              )}
                            </div>
                            <h3 className="text-white text-[12px] font-normal mb-1">
                              {step.template?.title || 'Workflow Step'}
                            </h3>
                            <p className="text-white text-[10px] opacity-50">
                              {getDaysUntil(step.dueDate)}
                            </p>
                          </div>
                          
                          {/* Edit Button */}
                          <button
                            onClick={() => handleEditClick(step)}
                            className="text-[#FF3B30] text-[10px] px-2 py-1 hover:bg-[rgba(255,59,48,0.1)] rounded transition-colors"
                          >
                            Edit
                          </button>
                        </div>

                        {/* Step Description */}
                        {step.template?.description && (
                          <p className="text-white text-[10px] opacity-70 mb-3">
                            {step.template.description}
                          </p>
                        )}

                        {/* Step Actions */}
                        {!isCompleted && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleCompleteStep(step.id)}
                              className="flex-1 bg-green-600 text-white text-[10px] font-medium py-2 rounded hover:bg-green-700 transition-all"
                            >
                              Completed
                            </button>
                            <button
                              onClick={() => handleSnoozeStep(step.id)}
                              className="flex-1 bg-[rgba(255,255,255,0.1)] text-white text-[10px] font-medium py-2 rounded hover:bg-[rgba(255,255,255,0.2)] transition-all"
                            >
                              Remind Later
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {filteredSteps.length === 0 && (
              <div className="text-center py-12">
                <p className="text-white text-[12px] opacity-50">
                  No steps in this category
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default WorkflowDetail;


